import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getUtilisateurs,
  createUtilisateur,
  updateUtilisateur,
  deleteUtilisateur,
  changerMotDePasse,
} from '../services/authService';
import { formatDateTime } from './devisUtils';
import '../assets/styles/Parametrage.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';

/**
 * Paramétrage > Accès : qui peut ouvrir la gestion interne.
 *
 * Deux cartes : le compte connecté (changement de mot de passe) et la
 * liste des comptes (création, modification, désactivation, suppression).
 * Les garde-fous sont côté serveur — on ne se supprime pas soi-même, on ne
 * ferme pas le dernier compte actif — l'écran ne fait que les refléter.
 */

const COMPTE_VIDE = { nom: '', identifiant: '', email: '', motDePasse: '', actif: true };
const MDP_VIDE = { actuel: '', nouveau: '', confirmation: '' };

function compteToForm(compte) {
  return {
    nom: compte.nom ?? '',
    identifiant: compte.identifiant ?? '',
    email: compte.email ?? '',
    motDePasse: '',
    actif: compte.actif ?? true,
  };
}

function ParametrageAcces() {
  const { utilisateur, setUtilisateur } = useAuth();

  const [comptes, setComptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(COMPTE_VIDE);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });

  const [suppression, setSuppression] = useState(null);

  const [mdpOuvert, setMdpOuvert] = useState(false);
  const [mdp, setMdp] = useState(MDP_VIDE);
  const [mdpError, setMdpError] = useState(null);

  function charger() {
    setLoading(true);
    setError(null);
    return getUtilisateurs()
      .then((res) => setComptes(res.data || []))
      .catch((err) => setError(err.message || 'Impossible de charger les comptes.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    charger();
  }, []);

  function ouvrirCreation() {
    setEdition({ id: null });
    setForm(COMPTE_VIDE);
    setFormError(null);
  }

  function ouvrirEdition(compte) {
    setEdition({ id: compte.id });
    setForm(compteToForm(compte));
    setFormError(null);
  }

  function fermerModale() {
    setEdition(null);
    setFormError(null);
  }

  function handleFieldChange(champ) {
    return (e) => {
      const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [champ]: valeur }));
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const payload = {
      nom: form.nom,
      identifiant: form.identifiant,
      email: form.email,
      actif: form.actif,
    };
    // En édition, un champ vide veut dire « ne pas changer ».
    if (form.motDePasse || edition.id === null) payload.motDePasse = form.motDePasse;

    try {
      if (edition.id === null) {
        const res = await createUtilisateur(payload);
        setFlash(`Compte ${res.data.identifiant} créé.`);
      } else {
        const res = await updateUtilisateur(edition.id, payload);
        setFlash(`Compte ${res.data.identifiant} mis à jour.`);
        // Son propre nom ou email : la barre latérale doit suivre.
        if (utilisateur && res.data.id === utilisateur.id) setUtilisateur(res.data);
      }
      await charger();
      setEdition(null);
    } catch (err) {
      setFormError(err.message || 'Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteUtilisateur(suppression.id);
      await charger();
      setFlash(`Compte ${suppression.identifiant} supprimé.`);
      setSuppression(null);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setSaving(false);
    }
  }

  function ouvrirMdp() {
    setMdp(MDP_VIDE);
    setMdpError(null);
    setMdpOuvert(true);
  }

  async function handleMdp(e) {
    e.preventDefault();
    if (mdp.nouveau !== mdp.confirmation) {
      setMdpError('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    setSaving(true);
    setMdpError(null);
    try {
      await changerMotDePasse(mdp.actuel, mdp.nouveau);
      setFlash('Votre mot de passe a été modifié.');
      setMdpOuvert(false);
    } catch (err) {
      setMdpError(err.message || 'Modification impossible.');
    } finally {
      setSaving(false);
    }
  }

  const editeSoiMeme = edition && utilisateur && edition.id === utilisateur.id;

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Accès</h1>
          <p className="param-subtitle">
            Comptes autorisés à ouvrir la gestion interne.
          </p>
        </div>
        <button type="button" className="param-btn param-btn-primary" onClick={ouvrirCreation}>
          + Ajouter un compte
        </button>
      </div>

      {flash && (
        <div className="param-toolbar">
          <span className="param-flash">{flash}</span>
        </div>
      )}

      <div className="param-scroll param-scroll-cartes">
        <div className="param-card">
          <div className="param-card-header">
            <div>
              <div className="param-section-titre">Mon compte</div>
              <div className="param-card-note">
                Connecté en tant que <strong>{utilisateur?.nom}</strong> ({utilisateur?.email}).
              </div>
            </div>
            <button type="button" className="param-btn param-btn-sm" onClick={ouvrirMdp}>
              Changer mon mot de passe
            </button>
          </div>
        </div>

        <div className="param-card">
          <div className="param-card-header">
            <div>
              <div className="param-section-titre">
                Comptes
                <span className="param-compteur">{comptes.length}</span>
              </div>
              <div className="param-card-note">
                Un compte désactivé est déconnecté et ne peut plus se connecter.
              </div>
            </div>
          </div>

          {loading && <div className="param-state">Chargement des comptes...</div>}
          {!loading && error && <div className="param-state param-state-error">{error}</div>}

          {!loading && !error && (
            <div className="param-table-wrap">
              <table className="param-table param-table-acces">
                <thead>
                  <tr>
                    <th className="param-th-intitule">NOM</th>
                    <th className="param-th-email">EMAIL</th>
                    <th className="param-th-etat">ÉTAT</th>
                    <th>DERNIER ACCÈS</th>
                    <th className="param-th-actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {comptes.map((compte) => {
                    const moi = utilisateur && compte.id === utilisateur.id;
                    return (
                      <tr key={compte.id}>
                        <td>
                          <div className="param-intitule">
                            {compte.nom}
                            {moi && <span className="param-tag">Vous</span>}
                          </div>
                          <div className="param-categorie">
                            <span className="param-code">{compte.identifiant}</span>
                          </div>
                        </td>
                        <td>{compte.email}</td>
                        <td>
                          <span className={`param-pill${compte.actif ? '' : ' param-pill-muted'}`}>
                            {compte.actif ? 'Actif' : 'Désactivé'}
                          </span>
                        </td>
                        <td className="param-nowrap">
                          {compte.dernier_acces ? formatDateTime(compte.dernier_acces) : 'Jamais'}
                        </td>
                        <td>
                          <div className="param-row-actions">
                            <button
                              type="button"
                              className="param-btn param-btn-sm"
                              onClick={() => ouvrirEdition(compte)}
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              className="param-btn param-btn-sm param-btn-danger"
                              onClick={() => setSuppression(compte)}
                              disabled={moi}
                              title={
                                moi ? 'Vous ne pouvez pas supprimer votre propre compte' : undefined
                              }
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {edition && (
        <div className="param-modal-overlay" onClick={fermerModale}>
          <div className="param-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">
                {edition.id === null ? 'Nouveau compte' : 'Modifier le compte'}
              </h2>
              <button type="button" className="param-modal-close" onClick={fermerModale}>
                ×
              </button>
            </div>

            <form className="param-form param-modal-body" onSubmit={handleSubmit}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && (
                <div className="param-form-message param-form-message-error">{formError}</div>
              )}

              <label className="param-field">
                <span className="param-label">Nom</span>
                <input
                  type="text"
                  className="param-input"
                  maxLength={150}
                  placeholder="Prénom Nom"
                  value={form.nom}
                  onChange={handleFieldChange('nom')}
                  required
                />
              </label>

              <div className="param-form-row">
                <label className="param-field">
                  <span className="param-label">Identifiant</span>
                  <input
                    type="text"
                    className="param-input"
                    maxLength={60}
                    autoComplete="off"
                    placeholder="prenom.nom"
                    value={form.identifiant}
                    onChange={handleFieldChange('identifiant')}
                    required
                  />
                </label>
                <label className="param-field param-field-grow">
                  <span className="param-label">Email</span>
                  <input
                    type="email"
                    className="param-input"
                    maxLength={255}
                    autoComplete="off"
                    value={form.email}
                    onChange={handleFieldChange('email')}
                    required
                  />
                </label>
              </div>

              <label className="param-field">
                <span className="param-label">
                  {edition.id === null
                    ? 'Mot de passe'
                    : 'Nouveau mot de passe (vide : inchangé)'}
                </span>
                <input
                  type="password"
                  className="param-input"
                  autoComplete="new-password"
                  minLength={form.motDePasse || edition.id === null ? 8 : undefined}
                  value={form.motDePasse}
                  onChange={handleFieldChange('motDePasse')}
                  required={edition.id === null}
                />
              </label>
              <p className="param-hint">Au moins 8 caractères.</p>

              <label className="param-checkbox-field">
                <input
                  type="checkbox"
                  checked={form.actif}
                  onChange={handleFieldChange('actif')}
                  disabled={Boolean(editeSoiMeme)}
                />
                <span>
                  Compte actif (peut se connecter)
                  {editeSoiMeme ? ' — votre propre compte reste actif' : ''}
                </span>
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement du compte" estimation />
              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={fermerModale} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Créer' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {suppression && (
        <div className="param-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Supprimer le compte</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Voulez-vous vraiment supprimer le compte de{' '}
                <strong>{suppression.nom}</strong> ({suppression.identifiant}) ? Cette personne ne
                pourra plus se connecter.
              </p>
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setSuppression(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mdpOuvert && (
        <div className="param-modal-overlay" onClick={() => setMdpOuvert(false)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Changer mon mot de passe</h2>
              <button
                type="button"
                className="param-modal-close"
                onClick={() => setMdpOuvert(false)}
              >
                ×
              </button>
            </div>

            <form className="param-form param-modal-body" onSubmit={handleMdp}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {mdpError && (
                <div className="param-form-message param-form-message-error">{mdpError}</div>
              )}

              <label className="param-field">
                <span className="param-label">Mot de passe actuel</span>
                <input
                  type="password"
                  className="param-input"
                  autoComplete="current-password"
                  value={mdp.actuel}
                  onChange={(e) => setMdp((p) => ({ ...p, actuel: e.target.value }))}
                  required
                />
              </label>
              <label className="param-field">
                <span className="param-label">Nouveau mot de passe</span>
                <input
                  type="password"
                  className="param-input"
                  autoComplete="new-password"
                  minLength={8}
                  value={mdp.nouveau}
                  onChange={(e) => setMdp((p) => ({ ...p, nouveau: e.target.value }))}
                  required
                />
              </label>
              <label className="param-field">
                <span className="param-label">Confirmer le nouveau mot de passe</span>
                <input
                  type="password"
                  className="param-input"
                  autoComplete="new-password"
                  minLength={8}
                  value={mdp.confirmation}
                  onChange={(e) => setMdp((p) => ({ ...p, confirmation: e.target.value }))}
                  required
                />
              </label>

              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setMdpOuvert(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : 'Modifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParametrageAcces;
